/**
 * audio/recipes-reward.js — every sound that tells the player they did well.
 *
 * GDD 11.1 pillar "Feedback clarity": score, tickets, prizes and set completion
 * each need a distinct celebratory shape, and they escalate — a set piece is a
 * sting, a completed set is a fanfare with a goose on the end of it.
 *
 * @module audio/recipes-reward
 */

import {
  osc, noiseBurst, fm, adsr, out, filter, reverb, feedbackDelay, send,
  arange, arand, mtof,
} from './synth.js';
import { honk } from './recipes-core.js';
import { bubbles } from './recipes-impact.js';

/** @param {number} v @param {number} lo @param {number} hi */
function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * A stacked note — detuned squares through a lowpass over a triangle. Reads as
 * a fairground organ rather than a synth lead.
 * @returns {number} End time.
 */
function organNote(ctx, dest, { note, t0, gain = 0.16, decay = 0.3, pan = 0, bright = 1 }) {
  const f = mtof(note);
  const g = out(ctx, dest, pan);
  const lp = filter(ctx, 'lowpass', clamp(1400 * bright, 500, 9000), 0.9);
  lp.connect(g);

  for (const det of [-6, 5]) {
    const n = ctx.createOscillator();
    n.type = 'square';
    n.detune.value = det;
    n.frequency.value = f;
    const ng = ctx.createGain();
    ng.gain.value = 0.5;
    n.connect(ng);
    ng.connect(lp);
    n.start(t0);
    n.stop(t0 + decay + 0.1);
  }
  const tri = ctx.createOscillator();
  tri.type = 'triangle';
  tri.frequency.value = f * 2;
  const tg = ctx.createGain();
  tg.gain.value = 0.25;
  tri.connect(tg);
  tg.connect(lp);
  tri.start(t0);
  tri.stop(t0 + decay + 0.1);

  return adsr(g.gain, t0, { peak: gain, attack: 0.008, hold: decay * 0.3, decay: decay * 0.8 });
}

/**
 * A run of organ notes.
 * @returns {number} End time.
 */
function fanfare(ctx, dest, notes, t0, o = {}) {
  const { gain = 0.16, spacing = 0.13, decay = 0.34, pan = 0, bright = 1 } = o;
  let end = t0;
  notes.forEach((note, i) => {
    const at = t0 + i * spacing;
    const last = i === notes.length - 1;
    end = Math.max(end, organNote(ctx, dest, {
      note, t0: at, gain, pan, bright,
      decay: last ? decay * 2.2 : decay,
    }));
    if (last) {
      // Octave sparkle on the landing note.
      osc(ctx, dest, {
        type: 'sine', freq: mtof(note + 12), t0: at,
        gain: gain * 0.45, attack: 0.006, decay: decay * 1.8, pan,
      });
    }
  });
  return end;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoring
// ─────────────────────────────────────────────────────────────────────────────

/** Points → one of five chime pitches. Bigger scores ring higher. */
function scoreNote(points) {
  const p = Number(points) || 0;
  if (p <= 0) return 69;
  if (p < 25) return 76;
  if (p < 75) return 79;
  if (p < 150) return 83;
  if (p < 300) return 86;
  return 88;
}

/** Score popup — a bright FM bell, pitched by the points awarded. */
export function scoreChime(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const note = scoreNote(o.points);
  const f = mtof(note);
  // Not `opts.volume` — the engine already scales the whole voice with it.
  const v = 1;

  fm(ctx, dest, {
    carrier: f, ratio: 3.5, index: 420, indexTo: 1,
    t0, gain: 0.17 * v, attack: 0.002, decay: 0.42, pan,
  });
  osc(ctx, dest, {
    type: 'sine', freq: f * 1.5, t0: t0 + 0.012,
    gain: 0.07 * v, attack: 0.003, decay: 0.34, pan,
  });
  osc(ctx, dest, {
    type: 'sine', freq: f * 2, t0: t0 + 0.02,
    gain: 0.04 * v, attack: 0.003, decay: 0.22, pan,
  });
  return t0 + 0.56;
}

/**
 * Ticket tally — a cascade of ticks that speeds up, then lands on a chime.
 * The count scales with the payout so a big round audibly takes longer.
 */
export function ticketTally(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const n = clamp(Math.round(o.count != null ? o.count : 14), 4, 26);
  let at = t0;
  let gap = 0.052;

  for (let i = 0; i < n; i++) {
    const k = 1 + (i / n) * 0.7;
    noiseBurst(ctx, dest, {
      t0: at, gain: 0.11, attack: 0.0006, decay: 0.022,
      type: 'bandpass', freq: 2100 * k, q: 3.4, pan: pan + arange(-0.12, 0.12),
    });
    osc(ctx, dest, {
      type: 'square', freq: 1500 * k, t0: at,
      gain: 0.035, attack: 0.0008, decay: 0.02, pan,
    });
    at += gap;
    gap = Math.max(0.028, gap * 0.965);
  }
  osc(ctx, dest, { type: 'sine', freq: mtof(88), t0: at, gain: 0.13, attack: 0.003, decay: 0.4, pan });
  osc(ctx, dest, { type: 'sine', freq: mtof(93), t0: at, gain: 0.06, attack: 0.003, decay: 0.3, pan });
  return at + 0.5;
}

/** Token award — a coin shimmer: high partials gliding up, then a small run. */
export function tokenAward(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  for (const [f, g] of [[2600, 0.09], [3450, 0.07], [4700, 0.05]]) {
    osc(ctx, dest, {
      type: 'sine', freq: f, freqTo: f * 1.14, glide: 0.22,
      t0, gain: g, attack: 0.004, decay: 0.32, pan,
    });
  }
  noiseBurst(ctx, dest, {
    t0, gain: 0.06, attack: 0.01, decay: 0.3,
    type: 'highpass', freq: 4200, q: 0.7, pan,
  });
  [84, 88, 91].forEach((note, i) => {
    osc(ctx, dest, {
      type: 'triangle', freq: mtof(note), t0: t0 + 0.06 + i * 0.055,
      gain: 0.10, attack: 0.003, decay: i === 2 ? 0.34 : 0.14, pan,
    });
  });
  return t0 + 0.62;
}

// ─────────────────────────────────────────────────────────────────────────────
// Prizes, sets and the big moments
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Prize earned — a rising fanfare whose length and brightness follow the tier.
 * Accepts either `opts.tier` or the GDD 7.4 prize `kind`.
 */
export function prizeTier(ctx, dest, o = {}, t0 = 0) {
  const kind = String(o.kind || '');
  const tier = o.tier
    || (kind.includes('large') ? 'big' : kind === 'fish' ? 'medium' : kind.includes('small') ? 'small' : 'medium');

  const shapes = {
    small: { notes: [72, 76, 79], gain: 0.14, spacing: 0.11, bright: 0.9 },
    medium: { notes: [69, 73, 76, 81], gain: 0.16, spacing: 0.12, bright: 1.1 },
    big: { notes: [64, 69, 73, 76, 81], gain: 0.18, spacing: 0.13, bright: 1.4 },
  };
  const s = shapes[tier] || shapes.medium;
  const end = fanfare(ctx, dest, s.notes, t0, { ...s, pan: o.pan || 0 });

  if (tier === 'big') {
    const fx = reverb(ctx, { decay: 1.6 });
    const tap = out(ctx, dest, 0);
    send(ctx, tap, fx, 0.3, dest, end + 1.2);
    fanfare(ctx, tap, s.notes, t0, { ...s, gain: s.gain * 0.5 });
    noiseBurst(ctx, dest, {
      t0: t0 + s.spacing * (s.notes.length - 1), gain: 0.08,
      attack: 0.02, decay: 0.6, type: 'highpass', freq: 3600,
    });
  }
  return end + 0.2;
}

/**
 * Set piece earned — a three-note sting that climbs as the set fills up.
 * `opts.progress` is 0–1; at 1 it sits a fifth above where it started.
 */
export function setPieceSting(ctx, dest, o = {}, t0 = 0) {
  const progress = clamp(Number(o.progress) || 0, 0, 1);
  const lift = Math.round(progress * 7);
  const notes = [69 + lift, 73 + lift, 76 + lift];
  const end = fanfare(ctx, dest, notes, t0, {
    gain: 0.15, spacing: 0.115, decay: 0.3,
    bright: 0.9 + progress * 0.8, pan: o.pan || 0,
  });
  osc(ctx, dest, {
    type: 'sine', freq: mtof(88 + lift), t0: t0 + 0.23,
    gain: 0.05 + progress * 0.05, attack: 0.005, decay: 0.5,
  });
  return end + 0.15;
}

/** Set completed — the biggest fanfare in the game, and then a goose. */
export function setComplete(ctx, dest, o = {}, t0 = 0) {
  const notes = [60, 64, 67, 72, 76, 79];
  const fx = reverb(ctx, { decay: 2.0 });
  const echo = feedbackDelay(ctx, { time: 0.26, feedback: 0.3 });
  const tap = out(ctx, dest, 0);
  const end = fanfare(ctx, tap, notes, t0, { gain: 0.17, spacing: 0.135, decay: 0.5, bright: 1.5 });
  send(ctx, tap, fx, 0.34, dest, end + 2.2);
  send(ctx, tap, echo, 0.20, dest, end + 1.6);

  // Crash on the landing chord.
  noiseBurst(ctx, dest, {
    t0: t0 + 0.135 * (notes.length - 1), gain: 0.13,
    attack: 0.006, decay: 0.9, type: 'highpass', freq: 3000, q: 0.6,
  });
  // The honk. Non-negotiable.
  const honkAt = t0 + 0.135 * notes.length + 0.22;
  honk(ctx, dest, { pitch: 1.06, gain: 1.1 }, honkAt);
  honk(ctx, dest, { pitch: 0.92, gain: 0.7, pan: -0.3 }, honkAt + 0.34);
  return honkAt + 0.95;
}

/**
 * High Striker bell — a long FM bell over a crowd swell. GDD 11.2 Critical,
 * and the loudest single event the game produces.
 */
export function bellRing(ctx, dest, o = {}, t0 = 0) {
  const fx = reverb(ctx, { decay: 2.4, damp: 4200 });
  const tap = out(ctx, dest, 0);
  send(ctx, tap, fx, 0.30, dest, t0 + 3.4);

  fm(ctx, tap, {
    carrier: 392, ratio: 1.41, index: 1250, indexTo: 3,
    t0, gain: 0.26, attack: 0.001, decay: 2.1,
  });
  fm(ctx, tap, {
    carrier: 587, ratio: 2.76, index: 700, indexTo: 2,
    t0: t0 + 0.008, gain: 0.11, attack: 0.001, decay: 1.4,
  });
  noiseBurst(ctx, dest, {
    t0, gain: 0.14, attack: 0.0008, decay: 0.09,
    type: 'highpass', freq: 4000,
  });
  crowdCheer(ctx, dest, { gain: 1, duration: 1.8 }, t0 + 0.18);
  return t0 + 3.2;
}

/**
 * Crowd cheer — a filtered noise swell with scattered "voice" blips over it.
 * Real crowd samples are the obvious solution; this is the procedural one, and
 * the blips are what stop the swell reading as plain wind.
 */
export function crowdCheer(ctx, dest, o = {}, t0 = 0) {
  const dur = clamp(o.duration != null ? o.duration : 1.6, 0.4, 4);
  const gain = (o.gain != null ? o.gain : 1);
  const pan = o.pan || 0;

  noiseBurst(ctx, dest, {
    color: 'pink', t0, gain: 0.15 * gain,
    attack: dur * 0.22, hold: dur * 0.2, decay: dur * 0.6,
    type: 'bandpass', freq: 700, freqTo: 1500, q: 0.7, pan,
  });
  noiseBurst(ctx, dest, {
    color: 'white', t0: t0 + dur * 0.1, gain: 0.06 * gain,
    attack: dur * 0.2, decay: dur * 0.6,
    type: 'highpass', freq: 2400, q: 0.6, pan,
  });
  const voices = Math.round(10 + dur * 4);
  for (let i = 0; i < voices; i++) {
    const at = t0 + arand() * dur * 0.9;
    const f = arange(260, 720);
    osc(ctx, dest, {
      type: 'sawtooth', freq: f, freqTo: f * arange(0.85, 1.3), glide: 0.1,
      t0: at, gain: 0.022 * gain, attack: 0.02, decay: 0.16,
      pan: pan + arange(-0.7, 0.7),
    });
  }
  return t0 + dur + 0.3;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stall-specific wins
// ─────────────────────────────────────────────────────────────────────────────

/** Golden duck — a sparkle arpeggio with an echo trail. */
export function goldenDuck(ctx, dest, o = {}, t0 = 0) {
  const echo = feedbackDelay(ctx, { time: 0.135, feedback: 0.38 });
  const tap = out(ctx, dest, 0);
  const scale = [72, 76, 79, 84, 88, 91, 96, 100];
  send(ctx, tap, echo, 0.35, dest, t0 + 1.6);

  scale.forEach((note, i) => {
    osc(ctx, tap, {
      type: 'triangle', freq: mtof(note), t0: t0 + i * 0.058,
      gain: 0.11, attack: 0.002, decay: i === scale.length - 1 ? 0.5 : 0.16,
      pan: -0.3 + (i / scale.length) * 0.6,
    });
    osc(ctx, dest, {
      type: 'sine', freq: mtof(note + 12), t0: t0 + i * 0.058,
      gain: 0.03, attack: 0.002, decay: 0.12,
    });
  });
  noiseBurst(ctx, dest, {
    t0, gain: 0.05, attack: 0.02, decay: 0.7, type: 'highpass', freq: 5200,
  });
  return t0 + 1.2;
}

/** Fish won — a bubbly chime, warmer and rounder than the score bell. */
export function fishWin(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  bubbles(ctx, dest, { count: 4, spread: 0.3, pan, gain: 1.1 }, t0);
  [76, 81, 84].forEach((note, i) => {
    osc(ctx, dest, {
      type: 'sine', freq: mtof(note), t0: t0 + 0.1 + i * 0.09,
      gain: 0.13, attack: 0.006, decay: i === 2 ? 0.5 : 0.2, pan,
    });
  });
  osc(ctx, dest, {
    type: 'triangle', freq: mtof(88), t0: t0 + 0.28,
    gain: 0.05, attack: 0.006, decay: 0.42, pan,
  });
  return t0 + 0.85;
}

/** Fish naming prompt (GDD 11.2) — a gentle chime with a small splash under it. */
export function fishName(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  osc(ctx, dest, { type: 'sine', freq: mtof(81), t0, gain: 0.10, attack: 0.01, decay: 0.5, pan });
  osc(ctx, dest, { type: 'sine', freq: mtof(88), t0: t0 + 0.11, gain: 0.07, attack: 0.01, decay: 0.44, pan });
  bubbles(ctx, dest, { count: 3, spread: 0.35, pan, gain: 0.8 }, t0 + 0.06);
  noiseBurst(ctx, dest, {
    t0: t0 + 0.04, gain: 0.07, attack: 0.01, decay: 0.18,
    type: 'bandpass', freq: 1600, freqTo: 2600, q: 0.9, pan,
  });
  return t0 + 0.72;
}
